import { useEffect, useState } from "react"
import { WasmRunner } from "@coherentglobal/wasm-runner"
import { Tab, Tabs, TabList, TabPanel } from "react-tabs"
import { InstallButton } from "./components/InstallButton"
import { DashboardIcon } from "./icons/DashboardIcon"
import { PerformanceIcon } from "./icons/PerformanceIcon"
import { KeyboardIcon } from "./icons/KeyboardIcon"
import { PerformanceForm } from "./components/PerformanceForm"
import { DashboardTab } from "./components/dashboard/DashboardTab"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { AboutTab } from "./components/AboutTab"
import { ShareButton } from "./components/ShareButton"
import { QRCodeButton } from "./components/QRCodeButton"
import { ToastContainer } from "react-toastify"
import { logEvent } from "./utils/logEvent"
import { ArrowUpIcon } from "./icons/ArrowUpIcon"
import { ArrowDownIcon } from "./icons/ArrowDownIcon"
import { Doughnut } from "react-chartjs-2"
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js"

import "react-toastify/dist/ReactToastify.css"
import { InputJson } from "./components/dashboard/InputJson"
import DoughnutSections from "./defines/Doughnut"
import { fetchValidationData } from "./fetchers/fetchValidationData"
import { fetchVersions } from "./fetchers/fetchVersions"
import { generateWasmLink } from "./utils/generateWasmLink"

const App = () => {
  ChartJS.register(ArcElement, Tooltip, Legend)
  const options = {
    plugins: {
      legend: {
        display: false,
      },
    },
  }

  const [runner, setRunner] = useState<null | WasmRunner>(null)
  const [request, setRequest] = useState<any>(null)
  const [response, setResponse] = useState<any>({})
  const [tabIndex, setTabIndex] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [id, setId] = useState<string>('')
  const [jsonViewModal, setJsonViewModal] = useState<boolean>(false)
  const [doughnutChartArray, setDoughnutChartArray] = useState([0, 0, 0, 0, 0])

  const initWasmRunner = async () => {
    const versions = await fetchVersions();
    const id = versions.data[0].id;
    setId(id);
    const validation = await fetchValidationData(id);
    const serviceId = validation.response_meta.service_id;

    const wasmRunner = new WasmRunner({
      id: id,
      url: generateWasmLink(serviceId),
    })
    await wasmRunner.initialize()
    setRunner(wasmRunner)
  }

  useEffect(() => {
    const init = async () => {
      await initWasmRunner()
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (response?.response_data) {
      const data = response?.response_data?.outputs
      setDoughnutChartArray([data.MaxPIPayment, request.PptyTax, request.MonthlyHomeIns, 0, 0])
    }
  }, [response, request])

  const currencyWithComma = (num: number, hasSpace = false) => {
    if (num) {
      return `$${hasSpace ? " " : ""}${Number(num)
        .toFixed(0)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
    }
    return "$ -"
  }

  const renderDoughnutLabel = (price: number, section: { name: string; color: string }) => {
    return (
      <div className="flex flex-row mb-2 pl-2.5 bg-form-medium border-l-8" style={{ borderColor: section.color }}>
        <div className="py-2 font-bold w-5/12 border-r border-form-dark text-sm">{currencyWithComma(price, true)}</div>
        <div className="py-2 pl-2.5 w-2/4 text-sm">{section.name}</div>
      </div>
    )
  }

  const renderModals = () => {
    if (tabIndex === 0) {
      return isModalOpen ? (
        <>
          <div
            className="absolute bottom-1 h-full w-full bg-neutral-800 bg-opacity-80"
            onClick={() => setIsModalOpen(false)}
          />
          <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 max-w-full w-104 h-5/6 bg-white rounded-t-2xl py-4">
            <div className="relative overflow-hidden h-full">
              <div className="flex flex-row mb-5 px-6 cursor-pointer" onClick={() => setIsModalOpen(false)}>
                <div className="w-10 h-10 rounded-full bg-violet-100 p-2">
                  <KeyboardIcon />
                </div>
                <div className="ml-4">
                  <div className="mt-1 font-semibold text-xs text-slate-800">
                    COST
                    <br />
                    BREAKDOWN
                  </div>
                </div>
                <div className="absolute top-5 right-9">
                  <ArrowDownIcon />
                </div>
              </div>
              <div className="flex pt-1 mx-auto w-full border-t border-slate-100">
                <button
                  className={`${!jsonViewModal && "border-black"} border-b py-2 text-sm w-1/2`}
                  type="button"
                  onClick={() => setJsonViewModal(false)}
                >
                  FIELDS
                </button>
                <button
                  className={`${jsonViewModal && "border-black"} border-b py-2 text-sm w-1/2`}
                  type="button"
                  onClick={() => setJsonViewModal(true)}
                >
                  JSON VIEW
                </button>
              </div>
              <div className="pb-24 overflow-scroll h-full">
                {jsonViewModal ? (
                  <div className="relative px-6 py-4">
                    <InputJson defaultValues={response} />
                  </div>
                ) : (
                  <div className="relative px-6 py-4">
                    <div className="py-6 px-4 rounded bg-form-medium text-center">
                      <div className="font-bold text-2xl leading-6">
                        {currencyWithComma(response?.response_data?.outputs?.MaxHomePriceOnFunds.toFixed(0))}
                      </div>
                      <div className="mt-3 leading-4 text-sm">Estimated Home Price</div>
                      <button className="text-sm underline text-brand-light" onClick={() => setIsModalOpen(false)}>
                        Recalculate
                      </button>
                    </div>
                    <div className="mt-2 flex flex-row">
                      <div className="mr-1 py-4 bg-form-medium w-full rounded text-center">
                        <div className="font-bold text-xl leading-6">
                          {currencyWithComma(response?.response_data?.outputs?.MaxLoanAmt.toFixed(0))}
                        </div>
                        <div className="mt-1 leading-4 text-xs">Loan Amount</div>
                      </div>
                      <div className="ml-1 py-4 bg-form-medium w-full rounded text-center">
                        <div className="font-bold text-xl leading-6">
                          {currencyWithComma(response?.response_data?.outputs?.DPBasedOnFunds.toFixed(0))}
                        </div>
                        <div className="mt-1 leading-4 text-xs">Down Payment</div>
                      </div>
                    </div>
                    <div className="flex relative justify-center mt-8">
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-28 w-28 bg-slate-200 rounded-full" />
                      <div className="absolute text-center top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-sm h-24 w-24 bg-white rounded-full pt-6">
                        Cost
                        <br />
                        Breakdown
                      </div>
                      <div className="relative w-52">
                        <Doughnut
                          data={{
                            labels: [
                              DoughnutSections.PiPayment.name,
                              DoughnutSections.PropertyTax.name,
                              DoughnutSections.Insurance.name,
                              DoughnutSections.PMI.name,
                              DoughnutSections.HOA.name,
                              "Other",
                            ],
                            datasets: [
                              {
                                label: "# of Votes",
                                data: doughnutChartArray,
                                backgroundColor: [
                                  DoughnutSections.PiPayment.color,
                                  DoughnutSections.PropertyTax.color,
                                  DoughnutSections.Insurance.color,
                                  DoughnutSections.PMI.color,
                                  DoughnutSections.HOA.color,
                                ],
                                borderColor: [
                                  DoughnutSections.PiPayment.color,
                                  DoughnutSections.PropertyTax.color,
                                  DoughnutSections.Insurance.color,
                                  DoughnutSections.PMI.color,
                                  DoughnutSections.HOA.color,
                                ],
                                borderWidth: 1,
                              },
                            ],
                          }}
                          options={options}
                        />
                      </div>
                    </div>
                    <div className={"mt-5"}>
                      {renderDoughnutLabel(doughnutChartArray[0], DoughnutSections.PiPayment)}
                      {renderDoughnutLabel(doughnutChartArray[1], DoughnutSections.PropertyTax)}
                      {renderDoughnutLabel(doughnutChartArray[2], DoughnutSections.Insurance)}
                    </div>
                    <button className="mt-4 text-sm underline text-brand-light" onClick={() => setIsModalOpen(false)}>
                      Recalculate
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div
          className="flex flex-row absolute bottom-24 left-1/2 transform -translate-x-1/2 max-w-full w-104 h-20 bg-white rounded-t-2xl py-4 px-6 cursor-pointer shadow-slide"
          onClick={() => setIsModalOpen(true)}
        >
          <div className="w-10 h-10 rounded-full bg-violet-100 p-2">
            <KeyboardIcon />
          </div>
          <div className="ml-4">
            <div className="font-bold text-xl leading-6">
              {currencyWithComma(response?.response_data?.outputs?.MaxHomePriceOnFunds.toFixed(0))}
            </div>
            <div className="font-semibold text-xs text-slate-800">ESTIMATED HOME PRICE</div>
          </div>
          <div className="absolute top-9 right-9">
            <ArrowUpIcon />
          </div>
        </div>
      )
    }
    return
  }

  return (
    <div className="h-full bg-form-medium">
      <div className="bg-form-light w-full px-3 h-16 flex items-center justify-between pt-1">
        <a
          href="https://coherent.global/"
          target="_blank"
          rel="noreferrer"
          onClick={() => logEvent("DemoAppLogoClick")}
        >
          <img src="/coherent-logo.png" alt="Coherent" className="h-8" />
        </a>
        <div className="flex space-x-3">
          <QRCodeButton />
          <ShareButton />
          <div className="install-button-container">
            <InstallButton />
          </div>
        </div>
      </div>

      <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
        <TabPanel className="space-y-6">
          <DashboardTab
          id={id}
            request={request}
            response={response.response_data ? response : null}
            setResponse={setResponse}
            setRequest={setRequest}
            setTabIndex={setTabIndex}
            initWasmRunner={initWasmRunner}
            runner={runner!}
          />
        </TabPanel>
        <TabPanel>
          <PerformanceForm runner={runner!}
          id={id}
          />
        </TabPanel>
        <TabPanel>
          <AboutTab />
        </TabPanel>
        {renderModals()}
        <TabList>
          <Tab onClick={() => logEvent("DemoAppTabClick", { tab: "DASHBOARD" })}>
            <div className="absolute top-1/2 -translate-y-1/2 w-full flex flex-col items-center justify-center">
              <DashboardIcon />
              <span className="mt-2 text-xs">DASHBOARD</span>
            </div>
          </Tab>
          <Tab onClick={() => logEvent("DemoAppTabClick", { tab: "PERFORMANCE" })}>
            <div className="absolute top-1/2 -translate-y-1/2 w-full flex flex-col items-center justify-center">
              <PerformanceIcon />
              <span className="mt-2 text-xs">PERFORMANCE</span>
            </div>
          </Tab>
          <Tab onClick={() => logEvent("DemoAppTabClick", { tab: "ABOUT" })}>
            <div className="absolute top-1/2 -translate-y-1/2 w-full flex flex-col items-center justify-center">
              <InformationCircleIcon className="h-6 w-6" />
              <span className="mt-2 text-xs">ABOUT</span>
            </div>
          </Tab>
        </TabList>
      </Tabs>
      <ToastContainer limit={1} />
    </div>
  )
}

export default App

